/* @flow */

// PayPal's pa.js analytics beacon uses fetch() with no .catch(), so a blocked
// beacon surfaces as an unhandled "Failed to fetch" rejection. Matches both the
// production (/pa/js/...) and staging (/staging/pa/js/...) script URLs.
const PA_JS_FILENAME = /^https?:\/\/(?:www\.)?paypalobjects\.com(?::\d+)?\/(?:[^?#]*\/)?pa\/js\//;
const FAILED_TO_FETCH = "Failed to fetch";

type Frame = { filename?: ?string };
type ExceptionValue = {
  value?: ?string,
  stacktrace?: ?{ frames?: ?Array<?Frame> }
};
type SentryEvent = {
  message?: ?string,
  exception?: ?{ values?: ?Array<?ExceptionValue> }
};

function exceptionValues(event: ?SentryEvent): Array<ExceptionValue> {
  const values = event && event.exception && event.exception.values;
  if (!Array.isArray(values)) {
    return [];
  }
  // reduce instead of filter(Boolean): Flow doesn't refine Array<?T> to Array<T>.
  return values.reduce((acc, value) => (value ? acc.concat(value) : acc), []);
}

function isPaJsFrame(frame: ?Frame): boolean {
  return Boolean(
    frame &&
      typeof frame.filename === "string" &&
      PA_JS_FILENAME.test(frame.filename)
  );
}

function hasPaJsFrame(values: Array<ExceptionValue>): boolean {
  return values.some(value => {
    const frames = value.stacktrace && value.stacktrace.frames;
    return Array.isArray(frames) && frames.some(isPaJsFrame);
  });
}

export default function sentryBeforeSend(event: ?SentryEvent): ?SentryEvent {
  const values = exceptionValues(event);
  const isFailedToFetch =
    (event && event.message === FAILED_TO_FETCH) ||
    values.some(value => value.value === FAILED_TO_FETCH);

  if (isFailedToFetch && hasPaJsFrame(values)) {
    return null;
  }
  return event;
}
